import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, ApiError, keys, useMeta } from '../../api.ts';
import { countryCodesSchema, countrySchema, type CountryCodesInput, type CountryInput } from '../../../shared/schemas.ts';
import { applyServerErrors, ConfirmButton, ErrorBox, Field, Loading, PageHeader, Spinner, useDocumentTitle, useToast } from '../../components/ui.tsx';

export function CountryEditPage() {
  const params = useParams();
  const id = params.id ? Number(params.id) : null;
  const meta = useMeta();
  const country = meta.data?.countries.find((c) => c.id === id);
  useDocumentTitle(country?.name ?? 'Uusi maa');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const form = useForm<CountryInput, unknown, z.output<typeof countrySchema>>({
    resolver: zodResolver(countrySchema),
    defaultValues: { name: '', code: '', dxcc: '', deleted: false },
  });
  useEffect(() => {
    if (country) form.reset({ name: country.name, code: country.code, dxcc: country.dxcc, deleted: country.deleted });
  }, [country, form]);

  const save = useMutation({
    mutationFn: (d: z.output<typeof countrySchema>) =>
      id ? api.put(`/admin/countries/${id}`, d).then(() => ({ id })) : api.post<{ id: number }>('/admin/countries', d),
    onSuccess: (r) => {
      toast.ok('Maa tallennettu');
      void qc.invalidateQueries({ queryKey: keys.meta });
      if (!id) navigate(`/admin/countries/${r.id}`);
    },
    onError: (err) => !applyServerErrors(err, form.setError) && toast.error(err),
  });
  const remove = useMutation({
    mutationFn: (force: boolean) => api.del(`/admin/countries/${id}${force ? '?force=1' : ''}`),
    onSuccess: () => {
      toast.ok('Maa poistettu');
      void qc.invalidateQueries({ queryKey: keys.meta });
      navigate('/admin/countries');
    },
  });

  const codes = useQuery({
    queryKey: ['countryCodes', id],
    queryFn: () => api.get<string[]>(`/admin/countries/${id}/codes`),
    enabled: !!id,
  });
  const codesForm = useForm<CountryCodesInput>({ resolver: zodResolver(countryCodesSchema), defaultValues: { codes: '', from: '', to: '' } });
  useEffect(() => {
    if (codes.data) codesForm.reset({ codes: codes.data.join('\n'), from: '', to: '' });
  }, [codes.data, codesForm]);
  const saveCodes = useMutation({
    mutationFn: (d: CountryCodesInput) => api.put<{ codes: string[] }>(`/admin/countries/${id}/codes`, d),
    onSuccess: (r) => {
      toast.ok(`${r.codes.length} prefiksiä tallennettu`);
      void codes.refetch();
    },
    onError: toast.error,
  });

  if (meta.isLoading) return <Loading />;
  if (id && !country) return <ErrorBox error={new Error('Maata ei löytynyt')} />;
  const e = form.formState.errors;
  const usedError = remove.error instanceof ApiError && remove.error.status === 409 ? remove.error : null;

  return (
    <div className="page page-narrow stack">
      <PageHeader title={country ? country.name : 'Uusi maa'} />
      <form className="card form" onSubmit={form.handleSubmit((d) => save.mutate(d))} noValidate>
        <div className="form-grid">
          <Field label="Nimi" error={e.name} htmlFor="name">
            <input id="name" className="input" {...form.register('name')} />
          </Field>
          <Field label="Pääprefiksi" error={e.code} htmlFor="code">
            <input id="code" className="input mono" {...form.register('code')} />
          </Field>
          <Field label="DXCC-numero" error={e.dxcc} hint="ADIF-tiedostojen DXCC-kenttä" htmlFor="dxcc">
            <input id="dxcc" className="input mono" {...form.register('dxcc')} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" {...form.register('deleted')} /> Poistunut maa (ei lasketa nykymaihin)
        </label>
        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? <Spinner /> : 'Tallenna'}
          </button>
          {id && (
            <>
              <span className="spacer" />
              <ConfirmButton title="Poista maa" message="Maa poistetaan pysyvästi. Mieluummin merkitse maa poistuneeksi." confirmText="Poista" onConfirm={() => remove.mutateAsync(false).catch(() => {})}>
                Poista maa
              </ConfirmButton>
            </>
          )}
        </div>
        {usedError && (
          <div className="alert alert-warn">
            {usedError.message}{' '}
            <button type="button" className="btn btn-sm btn-danger" onClick={() => remove.mutate(true)}>
              Poista silti QSO:ineen
            </button>
          </div>
        )}
      </form>

      {id && (
        <form className="card form" onSubmit={codesForm.handleSubmit((d) => saveCodes.mutate(d))} noValidate>
          <div className="card-title">
            <h2>Prefiksit</h2>
          </div>
          <p className="muted small" style={{ margin: 0 }}>
            Kutsun alusta tunnistetaan maa (1–3 merkkiä). Kirjoita prefiksit riveittäin tai anna alue, esim. <code>APA</code> – <code>ASZ</code>,{' '}
            <code>C2A</code> – <code>C2Z</code> tai <code>9EA</code> – <code>9FZ</code>.
          </p>
          <Field label="Prefiksit" htmlFor="codes">
            <textarea id="codes" className="mono" rows={8} {...codesForm.register('codes')} />
          </Field>
          <div className="form-grid">
            <Field label="Alue alkaen" htmlFor="from">
              <input id="from" className="input mono" {...codesForm.register('from')} />
            </Field>
            <Field label="Alue päättyen" htmlFor="to">
              <input id="to" className="input mono" {...codesForm.register('to')} />
            </Field>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={saveCodes.isPending}>
              Tallenna prefiksit
            </button>
            <span className="muted small">{codes.data?.length ?? 0} prefiksiä</span>
          </div>
        </form>
      )}
    </div>
  );
}
